import axios from "axios";
import { PaginatedResponse } from "../types/common";
import {
  Popup,
  CreatePopupRequest,
  UpdatePopupRequest,
  PopupFilter,
} from "../types/landing";

const API_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";

// Get auth token from localStorage
const getAuthToken = () => {
  const token = localStorage.getItem("token");
  return token;
};

// Get active popup for landing page (public)
export const getActivePopup = async (): Promise<Popup | null> => {
  try {
    const response = await axios.get(`${API_URL}/api/landing/popup`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching active popup:", error);
    return null;
  }
};

// Track popup interaction (public)
export const trackPopupInteraction = async (
  popupId: string,
  action: "click" | "dismiss"
): Promise<void> => {
  try {
    await axios.post(`${API_URL}/api/landing/popup/${popupId}/track`, {
      action,
    });
  } catch (error) {
    console.error("Error tracking popup interaction:", error);
  }
};

// Admin functions
export const popupService = {
  // Get all popups (admin)
  getAllPopups: async (
    filters: PopupFilter = {}
  ): Promise<PaginatedResponse<Popup>> => {
    try {
      const token = getAuthToken();
      const params = new URLSearchParams();

      if (filters.page) params.append("page", filters.page.toString());
      if (filters.limit) params.append("limit", filters.limit.toString());
      if (filters.status) params.append("status", filters.status);

      const response = await axios.get(
        `${API_URL}/api/landing/popup/admin?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data;
      return {
        success: data.success,
        data: data.data || [],
        pagination: {
          currentPage: data.page || 1,
          totalPages: data.pages || 1,
          totalItems: data.total || 0,
          hasNext: (data.page || 1) < (data.pages || 1),
          hasPrev: (data.page || 1) > 1,
        },
      };
    } catch (error: any) {
      console.error("Error fetching popups:", error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch popups"
      );
    }
  },

  // Get single popup (admin)
  getPopup: async (id: string): Promise<Popup> => {
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${API_URL}/api/landing/popup/admin/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error("Error fetching popup:", error);
      throw new Error(error.response?.data?.message || "Failed to fetch popup");
    }
  },

  // Create popup
  createPopup: async (data: CreatePopupRequest): Promise<Popup> => {
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API_URL}/api/landing/popup/admin`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error("Error creating popup:", error);
      throw new Error(
        error.response?.data?.message || "Failed to create popup"
      );
    }
  },

  // Update popup
  updatePopup: async (id: string, data: UpdatePopupRequest): Promise<Popup> => {
    try {
      const token = getAuthToken();
      const response = await axios.put(
        `${API_URL}/api/landing/popup/admin/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error("Error updating popup:", error);
      throw new Error(
        error.response?.data?.message || "Failed to update popup"
      );
    }
  },

  // Delete popup
  deletePopup: async (id: string): Promise<void> => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/api/landing/popup/admin/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error: any) {
      console.error("Error deleting popup:", error);
      throw new Error(
        error.response?.data?.message || "Failed to delete popup"
      );
    }
  },

  // Publish popup
  publishPopup: async (id: string): Promise<Popup> => {
    try {
      const token = getAuthToken();
      const response = await axios.patch(
        `${API_URL}/api/landing/popup/admin/${id}/publish`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error("Error publishing popup:", error);
      throw new Error(
        error.response?.data?.message || "Failed to publish popup"
      );
    }
  },

  // Unpublish popup
  unpublishPopup: async (id: string): Promise<Popup> => {
    try {
      const token = getAuthToken();
      const response = await axios.patch(
        `${API_URL}/api/landing/popup/admin/${id}/unpublish`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.data;
    } catch (error: any) {
      console.error("Error unpublishing popup:", error);
      throw new Error(
        error.response?.data?.message || "Failed to unpublish popup"
      );
    }
  },
};
